import React, { useState, useCallback } from 'react';
// Using _Name prefix for imports used in JSX but flagged as unused by linter
import _Button from '../../../components/ui/Button';
import _Input from '../../../components/ui/Input';
import _Icon from '../../../components/AppIcon';
import { PROFILE_CONSTANTS } from '../constants/profileConstants';
import { normalizeArrayField } from '../utils/dataProcessing';

const SkillsTab = ({ 
  profileData, 
  _fieldErrors, // Used for displaying error messages (prefixed with _ since skills validation removed)
  _onInputChange, // Not currently used in this component
  onArrayAdd, 
  onArrayRemove 
}) => {
  const [newLanguage, setNewLanguage] = useState({ name: '', level: 'conversational' });
  const [newCertification, setNewCertification] = useState({ name: '', url: '' });

  // Safely get normalized arrays
  const languages = normalizeArrayField(profileData.languages);
  const certifications = normalizeArrayField(profileData.certifications);

  // Handle language addition
  const _handleLanguageAdd = useCallback(() => {
    // Ensure newLanguage.name is a string before trimming
    const trimmedName = typeof newLanguage.name === 'string' ? newLanguage.name.trim() : '';
    
    if (trimmedName && !languages.some(lang => {
      // Safely extract language name for comparison
      const langName = typeof lang === 'object' && lang !== null ? 
        (lang.name || '') : 
        (typeof lang === 'string' ? lang : '');
      
      return langName === trimmedName;
    })) {
      const languageObj = {
        name: trimmedName,
        level: newLanguage.level || 'conversational'
      };
      // Directly call onArrayAdd with the language object
      onArrayAdd('languages', languageObj);
      setNewLanguage({ name: '', level: 'conversational' });
    }
  }, [newLanguage, languages, onArrayAdd]);

  // Handle language removal
  const _handleLanguageRemove = useCallback((languageToRemove) => {
    // Pass the language object directly to onArrayRemove
    // This will use the enhanced array remove handler in the parent component
    onArrayRemove('languages', languageToRemove);
  }, [onArrayRemove]);

  // Handle certification addition
  const _handleCertificationAdd = useCallback(() => {
    // Ensure newCertification.name is a string before trimming
    const trimmedName = typeof newCertification.name === 'string' ? newCertification.name.trim() : '';
    const trimmedUrl = typeof newCertification.url === 'string' ? newCertification.url.trim() : '';
    
    if (trimmedName && !certifications.some(cert => {
      // Safely compare certification names to prevent object comparison issues
      const certName = typeof cert === 'object' && cert !== null ? 
        (cert.name || cert.toString && cert.toString() !== '[object Object]' ? cert.name || cert.toString() : JSON.stringify(cert)) : 
        (typeof cert === 'string' ? cert : '');
      
      return certName === trimmedName;
    })) {
      // Create certification object with name and optional URL
      const certificationObj = {
        name: trimmedName,
        url: trimmedUrl || null
      };
      // Directly call onArrayAdd with the certification object
      onArrayAdd('certifications', certificationObj);
      setNewCertification({ name: '', url: '' });
    }
  }, [newCertification, certifications, onArrayAdd]);

  return (
    <div className="space-y-8">
      {/* Certifications */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-6">Certifications</h3>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-text-primary mb-2">
            Add Certification
          </label>
          <div className="space-y-3">
            <div className="flex gap-2">
              <_Input
                value={typeof newCertification.name === 'string' ? newCertification.name : ''}
                onChange={(e) => setNewCertification(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Certification name"
                onKeyPress={(e) => e.key === 'Enter' && _handleCertificationAdd()}
              />
            </div>
            <div className="flex gap-2">
              <_Input
                value={typeof newCertification.url === 'string' ? newCertification.url : ''}
                onChange={(e) => setNewCertification(prev => ({ ...prev, url: e.target.value }))}
                placeholder="Certificate URL (optional)"
                onKeyPress={(e) => e.key === 'Enter' && _handleCertificationAdd()}
              />
              <_Button
                variant="outline"
                iconName="Plus"
                onClick={_handleCertificationAdd}
                disabled={!newCertification.name || typeof newCertification.name !== 'string' || !newCertification.name.trim()}
              >
                Add
              </_Button>
            </div>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-3">
            Your Certifications ({certifications.length})
          </label>
          <div className="space-y-2">
            {certifications.map((cert, index) => {
              // Safely extract certification name and URL for display
              const certName = typeof cert === 'object' && cert !== null ? 
                (cert.name || cert.toString && cert.toString() !== '[object Object]' ? cert.name || cert.toString() : JSON.stringify(cert)) : 
                (typeof cert === 'string' ? cert : '');
              
              const certUrl = typeof cert === 'object' && cert !== null ? 
                (cert.url || null) : null;
                
              return (
                <div
                  key={index}
                  className="bg-success-50 text-success-700 px-3 py-2 rounded-lg text-sm flex items-center justify-between"
                >
                  <div className="flex items-center gap-2 flex-1">
                    <_Icon name="Award" size={14} />
                    <div className="flex flex-col gap-1 flex-1">
                      <span className="font-medium">{certName}</span>
                      {certUrl && (
                        <a
                          href={certUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-success-600 hover:text-success-800 underline flex items-center gap-1"
                        >
                          <_Icon name="ExternalLink" size={10} />
                          View Certificate
                        </a>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => onArrayRemove('certifications', cert)}
                    className="text-success-500 hover:text-success-700 ml-2"
                  >
                    <_Icon name="X" size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Languages */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-6">Languages</h3>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-text-primary mb-2">
            Add Language
          </label>
          <div className="flex gap-2">
            <_Input
              value={typeof newLanguage.name === 'string' ? newLanguage.name : ''}
              onChange={(e) => setNewLanguage(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Language name"
              onKeyPress={(e) => e.key === 'Enter' && _handleLanguageAdd()}
            />
            <select
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={newLanguage.level}
              onChange={(e) => setNewLanguage(prev => ({ ...prev, level: e.target.value }))}
            >
              {PROFILE_CONSTANTS.LANGUAGE_LEVELS.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
            <_Button
              variant="outline"
              iconName="Plus"
              onClick={_handleLanguageAdd}
              disabled={!newLanguage.name || typeof newLanguage.name !== 'string' || !newLanguage.name.trim()}
            >
              Add
            </_Button>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-3">
            Your Languages ({languages.length})
          </label>
          <div className="space-y-2">
            {languages.map((lang, index) => {
              // Safely extract language name and level for display
              const langName = typeof lang === 'object' && lang !== null ? 
                (lang.name || '') : 
                (typeof lang === 'string' ? lang : '');
              
              const langLevel = typeof lang === 'object' && lang !== null ? 
                (lang.level || 'conversational') : 'conversational';
              
              return (
                <div
                  key={index}
                  className="bg-info-50 text-info-700 px-3 py-2 rounded-lg text-sm flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <_Icon name="Globe" size={14} />
                    <span>{langName}</span>
                    <span className="text-xs bg-info-100 px-2 py-1 rounded-full">{langLevel}</span>
                  </div>
                  <button
                    onClick={() => onArrayRemove('languages', lang)}
                    className="text-info-500 hover:text-info-700"
                  >
                    <_Icon name="X" size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillsTab;

